'use client';

import { AlertCircle, Info, ShieldAlert, X } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

import type { IamFriendlyError } from './friendly-errors';

/**
 * Persistent, in-context Alert for IAM errors.
 *
 * Replaces the one-shot `toast.error(err.message)` pattern across the
 * IAM panel: a toast fires once and is gone in 4 seconds, which is
 * the worst possible UX for a permission denial — the operator sees
 * a flash, doesn't have time to read it, and is left in a UI that
 * looks like nothing happened.
 *
 * This component renders an Alert that stays on screen until the
 * operator either retries (the parent clears the state) or closes
 * the dialog. Variant + icon are picked from the friendly mapping
 * (destructive / warning / info) so the visual weight matches the
 * severity (a self-promote attempt is "warning" amber, an out-of-
 * scope cross-partner mutation is "destructive" rose).
 *
 * The `dense` prop drops the border + softens the surface so the
 * alert sits cleanly inside an already-bordered surface (e.g. the
 * footer band of a sheet) without the doubled-up border look.
 */
export function IamErrorAlert({
    error,
    dense = false,
    className,
    onDismiss,
}: {
    error: IamFriendlyError;
    /** True = no border + softer surface, for use inside an already-bordered band. */
    dense?: boolean;
    className?: string;
    /**
     * If provided, renders a small dismiss button in the top-right
     * corner. Use it when the alert can be cleared without retrying
     * (the parent owns the state).
     */
    onDismiss?: () => void;
}) {
    const Icon =
        error.variant === 'info'
            ? Info
            : error.variant === 'warning'
                ? AlertCircle
                : ShieldAlert;

    const surfaceCx =
        error.variant === 'destructive'
            ? 'border-rose-200 bg-rose-50 text-rose-900 [&>svg]:text-rose-700'
            : error.variant === 'warning'
                ? 'border-amber-200 bg-amber-50 text-amber-900 [&>svg]:text-amber-700'
                : 'border-sky-200 bg-sky-50 text-sky-900 [&>svg]:text-sky-700';

    return (
        <Alert
            variant={error.variant === 'destructive' ? 'destructive' : 'default'}
            className={cn(
                'relative',
                surfaceCx,
                dense && 'border-0 bg-transparent',
                onDismiss && 'pr-9',
                className,
            )}
        >
            <Icon className="h-4 w-4" />
            <AlertTitle className="text-sm">{error.title}</AlertTitle>
            <AlertDescription className="text-xs leading-relaxed">
                {error.description}
                {error.suggestion && (
                    <div className="mt-1 text-[11px] opacity-80">
                        <span className="font-medium">Next:</span>{' '}
                        {error.suggestion}
                    </div>
                )}
            </AlertDescription>
            {onDismiss && (
                <button
                    type="button"
                    onClick={onDismiss}
                    aria-label="Dismiss"
                    className="absolute right-2 top-2 rounded p-1 opacity-70 transition hover:bg-black/5 hover:opacity-100"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            )}
        </Alert>
    );
}
